import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageBits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { PlusSquare, Receipt, ScanLine, AlertTriangle, FileText, Truck, Euro } from "lucide-react";

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border rounded-md p-5" style={{ borderColor: "#E2E8F0" }}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
          <div className="text-2xl font-bold mt-2 tabular-nums">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: accent || "#F1F5F9" }}>
          <Icon className="h-5 w-5" style={{ color: "#0A2540" }} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const s = stats || {};
  return (
    <div data-testid="dashboard">
      <PageHeader
        title="Dashboard"
        subtitle="Überblick über alle Aufträge, Status und Abrechnungssummen"
        actions={
          <Button
            data-testid="quick-new-order"
            className="h-12 px-5 bg-[#0A2540] hover:bg-[#113A63] text-white"
            onClick={() => nav("/orders/new")}
          >
            <PlusSquare className="h-4 w-4 mr-2" /> Neuer Auftrag
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Aufträge gesamt" value={s.total_orders ?? "—"} icon={FileText} />
        <Stat label="Erfasst" value={s.by_status?.Erfasst ?? "—"} icon={FileText} accent="#DBEAFE" />
        <Stat label="Abgerechnet" value={s.by_status?.Abgerechnet ?? "—"} icon={Receipt} accent="#DCFCE7" />
        <Stat label="Konflikte" value={s.by_status?.Konflikt ?? "—"} icon={AlertTriangle} accent="#FEF3C7" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <Stat label="Aufmaß Summe (Monat)" value={`€ ${(s.month_total ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}`} icon={Euro} />
        <Stat label="Mitarbeiter" value={s.total_users ?? "—"} icon={FileText} />
        <Stat label="Fahrzeuge" value={s.total_vehicles ?? "—"} icon={Truck} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <button
          data-testid="quick-ai"
          onClick={() => nav("/ai")}
          className="text-left bg-white border rounded-md p-5 hover:border-[#0A2540] transition-colors"
          style={{ borderColor: "#E2E8F0" }}
        >
          <ScanLine className="h-5 w-5 text-[#0A2540]" />
          <div className="mt-3 text-base font-bold">KI Screenshot Analyse</div>
          <p className="text-xs text-slate-500 mt-1">Lade KASYS Screenshots hoch — automatische Übernahme</p>
        </button>
        <button
          data-testid="quick-billing"
          onClick={() => nav("/billing")}
          className="text-left bg-white border rounded-md p-5 hover:border-[#0A2540] transition-colors"
          style={{ borderColor: "#E2E8F0" }}
        >
          <Receipt className="h-5 w-5 text-[#0A2540]" />
          <div className="mt-3 text-base font-bold">Mitarbeiterabrechnung</div>
          <p className="text-xs text-slate-500 mt-1">Verdienst pro Mitarbeiter — gefiltert nach Datum und Team</p>
        </button>
        <button
          data-testid="quick-conflicts"
          onClick={() => nav("/conflicts")}
          className="text-left bg-white border rounded-md p-5 hover:border-[#0A2540] transition-colors"
          style={{ borderColor: "#E2E8F0" }}
        >
          <AlertTriangle className="h-5 w-5 text-[#0A2540]" />
          <div className="mt-3 text-base font-bold">Konflikte</div>
          <p className="text-xs text-slate-500 mt-1">Aufträge mit Aufmaß-Differenzen prüfen</p>
        </button>
      </div>

      <div className="bg-white border rounded-md" style={{ borderColor: "#E2E8F0" }}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E2E8F0" }}>
          <h3 className="font-bold">Letzte Aufträge</h3>
          <Button variant="outline" className="h-9" onClick={() => nav("/orders")} data-testid="see-all-orders">
            Alle anzeigen
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Auftragsnr.</th>
                <th className="text-left px-5 py-3 font-semibold">KLS-ID</th>
                <th className="text-left px-5 py-3 font-semibold">Datum</th>
                <th className="text-left px-5 py-3 font-semibold">Typ</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Aufmaß</th>
              </tr>
            </thead>
            <tbody>
              {(s.recent_orders || []).map((o) => (
                <tr key={o.id} onClick={() => nav(`/orders/${o.id}`)} className="border-t hover:bg-slate-50 cursor-pointer" style={{ borderColor: "#E2E8F0" }}>
                  <td className="px-5 py-3 font-semibold">{o.auftragsnummer}</td>
                  <td className="px-5 py-3 mono">{o.kls_id}</td>
                  <td className="px-5 py-3">{o.ausfuehrungsdatum}</td>
                  <td className="px-5 py-3">{o.auftragstyp}</td>
                  <td className="px-5 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-5 py-3 text-right tabular-nums">€ {(o.aufmass_total || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
              {(!s.recent_orders || s.recent_orders.length === 0) && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400">Noch keine Aufträge</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
